/**
 * Periodic proactive observations. Mirrors `KokoProactiveObserver.swift`:
 *
 *   - Timer fires every `proactiveIntervalMinutes * 60` seconds.
 *   - Timer is reset whenever the user submits their own turn so Koko
 *     doesn't interrupt an active conversation.
 *   - Observations use a distinct system prompt (`WEB_OBSERVATION_PROMPT`)
 *     that asks Claude to *observe*, not answer.
 *   - Output renders in the dedicated text panel (never the small bubble),
 *     matching native's text-only display, and TTS is skipped.
 *   - Supports `[POINT:...]` tags so Koko can land on whatever he's
 *     observing about.
 *
 * ON by default per the user's explicit planning decision.
 */

import type { SettingsStore } from "../app/SettingsStore";

export interface ProactiveObserverOptions {
  settings: SettingsStore;
  onFire: () => void | Promise<void>;
}

export class ProactiveObserver {
  private readonly options: ProactiveObserverOptions;
  private timerId: number | null = null;
  private isRunning = false;

  constructor(options: ProactiveObserverOptions) {
    this.options = options;

    // Re-arm whenever the enabled toggle or interval changes so the
    // user sees their settings changes take effect immediately.
    this.options.settings.subscribe("proactiveEnabled", () => this.rearm());
    this.options.settings.subscribe("proactiveIntervalMinutes", () => this.rearm());
    this.options.settings.subscribe("sessionActive", () => this.rearm());
  }

  start(): void {
    this.isRunning = true;
    this.rearm();
  }

  stop(): void {
    this.isRunning = false;
    this.clearTimer();
  }

  /**
   * Call this whenever the user does something so the proactive timer
   * doesn't fire in the middle of an active interaction. Mirrors
   * `KokoProactiveObserver.resetTimer()` at native line 63-66.
   */
  resetTimer(): void {
    if (!this.isRunning) return;
    this.rearm();
  }

  private rearm(): void {
    this.clearTimer();
    if (!this.isRunning) return;
    if (!this.options.settings.get("sessionActive")) return;
    if (!this.options.settings.get("proactiveEnabled")) return;

    const intervalMinutes = Math.max(
      0.02, // test-only floor (~1s); normal range starts at 1 minute
      this.options.settings.get("proactiveIntervalMinutes"),
    );
    const intervalMs = intervalMinutes * 60 * 1000;
    this.timerId = window.setTimeout(async () => {
      this.timerId = null;
      try {
        await this.options.onFire();
      } finally {
        // Re-arm after the observation completes so we fire again at
        // the next interval (only if still enabled).
        this.rearm();
      }
    }, intervalMs);
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
}
